import { useState, useCallback, useRef } from 'react';
import { checkoutService } from '../services/checkoutService';

const RAZORPAY_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';

export function useRazorpay() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const scriptLoaded = useRef(false);

  const loadScript = useCallback(() => {
    return new Promise((resolve) => {
      if (scriptLoaded.current || window.Razorpay) {
        scriptLoaded.current = true;
        resolve(true);
        return;
      }

      const script = document.createElement('script');
      script.src = RAZORPAY_SCRIPT_URL;
      script.async = true;
      script.onload = () => {
        scriptLoaded.current = true;
        resolve(true);
      };
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }, []);

  const initiatePayment = useCallback(async ({
    addressId,
    deliveryPartner,
    deliveryPartnerTat,
    shippingCost,
    couponReservationId,
    userInfo,
    onSuccess,
    onFailure
  }) => {
    setLoading(true);
    setError(null);

    try {
      const isLoaded = await loadScript();
      if (!isLoaded) {
        throw new Error('Failed to load payment gateway');
      }

      const response = await checkoutService.createOrder(
        addressId,
        deliveryPartner,
        deliveryPartnerTat,
        shippingCost,
        couponReservationId
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to create order');
      }

      const { razorpayOrderId, razorpayKeyId, amount, currency, orderNumber } = response.data;

      const options = {
        key: razorpayKeyId,
        amount,
        currency,
        name: 'Kalpavriksha',
        description: `Order ${orderNumber}`,
        order_id: razorpayOrderId,
        prefill: {
          name: userInfo?.name || '',
          email: userInfo?.email || '',
          contact: userInfo?.phone || ''
        },
        theme: { color: '#C9A227' },
        handler: async (response) => {
          try {
            setLoading(true);
            const verifyResponse = await checkoutService.verifyPayment(
              response.razorpay_order_id,
              response.razorpay_payment_id,
              response.razorpay_signature
            );

            setLoading(false);
            if (verifyResponse.success && verifyResponse.data.success) {
              onSuccess?.(verifyResponse.data);
            } else {
              const errorMsg = verifyResponse.data?.message || 'Payment verification failed';
              setError(errorMsg);
              onFailure?.({ message: errorMsg });
            }
          } catch (err) {
            setLoading(false);
            setError(err.message);
            onFailure?.(err);
          }
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            onFailure?.({ message: 'Payment cancelled', cancelled: true });
          }
        }
      };

      const razorpay = new window.Razorpay(options);

      razorpay.on('payment.failed', (response) => {
        setLoading(false);
        const errorMsg = response.error?.description || 'Payment failed';
        setError(errorMsg);
        onFailure?.({ message: errorMsg, code: response.error?.code });
      });

      razorpay.open();
      setLoading(false);

    } catch (err) {
      setLoading(false);
      setError(err.message);
      onFailure?.(err);
    }
  }, [loadScript]);

  const clearError = useCallback(() => setError(null), []);

  return { initiatePayment, loading, error, clearError };
}

export default useRazorpay;
